const reasons = [
  {
    icon: 'ri-radar-line',
    title: '24/7 Proactive Monitoring',
    description: 'Threats caught and resolved before they become outages. Always watching — even at 3am on a Sunday.',
    tags: ['Real-time alerts', 'Zero blind spots', 'Instant escalation'],
    large: true,
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Local, Responsive Support',
    description: 'Real DFW-based team. No overseas call centers. Immediate response.',
    sub: 'DFW-Based. Never Outsourced.',
    large: false,
  },
  {
    icon: 'ri-database-2-line',
    title: 'Backup & Recovery Specialists',
    description: "We've recovered businesses from crypto attacks, hardware failures, and silent backup failures. We test everything.",
    sub: 'Tested. Not Just Assumed.',
    large: false,
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Focused on Business Continuity',
    description: 'Everything we do is built around one goal: keeping your business operational.',
    tags: ['Uptime first', 'Risk prevention', 'Rapid recovery'],
    large: true,
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Consistent, Personal Service',
    description: "You'll always reach someone who knows your business and infrastructure — not a ticket number.",
    large: false,
  },
  {
    icon: 'ri-check-double-line',
    title: '100% Satisfaction Guarantee',
    description: "If we don't fix it right, we make it right — at no charge.",
    large: false,
  },
];

const WhyChooseUs = () => {
  return (
    <section className="py-24 lg:py-32 bg-slate-50 relative">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end gap-6 mb-16">
          <div className="flex-1">
            <span className="text-teal-600 font-bold text-xs uppercase tracking-widest block mb-4">
              Why Mirrored Storage
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-slate-950 leading-[1.05] tracking-tight max-w-2xl">
              The Partner That Treats<br />
              Your Uptime Like Their Own
            </h2>
          </div>
          <p className="text-slate-500 text-base leading-relaxed max-w-sm lg:text-right">
            DFW businesses trust us because we deliver every single time. Not a helpdesk ticket — a real partner.
          </p>
        </div>

        {/* Asymmetric grid */}
        <div className="grid lg:grid-cols-3 gap-4">

          {/* Large card — col-span-2 */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-8 hover:border-teal-300 hover:shadow-sm transition-all duration-300 cursor-default relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/4 rounded-full blur-2xl pointer-events-none" />
            <div className="w-12 h-12 flex items-center justify-center bg-teal-50 border border-teal-100 rounded-xl mb-6">
              <i className="ri-radar-line text-xl text-teal-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">{reasons[0].title}</h3>
            <p className="text-slate-500 leading-relaxed mb-6 max-w-md">{reasons[0].description}</p>
            <div className="flex flex-wrap gap-2">
              {reasons[0].tags?.map((tag) => (
                <span key={tag} className="text-xs font-bold text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-3 py-1.5">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Small card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-teal-300 hover:shadow-sm transition-all duration-300 cursor-default flex flex-col justify-between">
            <div>
              <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl mb-5">
                <i className="ri-map-pin-2-line text-lg text-teal-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">{reasons[1].title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{reasons[1].description}</p>
            </div>
            <div className="mt-5 pt-5 border-t border-slate-100">
              <span className="text-teal-600 text-xs font-bold uppercase tracking-wider">{reasons[1].sub}</span>
            </div>
          </div>

          {/* Small card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-teal-300 hover:shadow-sm transition-all duration-300 cursor-default flex flex-col justify-between">
            <div>
              <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl mb-5">
                <i className="ri-database-2-line text-lg text-teal-600" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">{reasons[2].title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{reasons[2].description}</p>
            </div>
            <div className="mt-5 pt-5 border-t border-slate-100">
              <span className="text-teal-600 text-xs font-bold uppercase tracking-wider">{reasons[2].sub}</span>
            </div>
          </div>

          {/* Large card — col-span-2 */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-8 hover:border-teal-300 hover:shadow-sm transition-all duration-300 cursor-default relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-56 h-56 bg-teal-500/4 rounded-full blur-2xl pointer-events-none" />
            <div className="w-12 h-12 flex items-center justify-center bg-teal-50 border border-teal-100 rounded-xl mb-6">
              <i className="ri-shield-star-line text-xl text-teal-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">{reasons[3].title}</h3>
            <p className="text-slate-500 leading-relaxed mb-6 max-w-md">{reasons[3].description}</p>
            <div className="flex flex-wrap gap-2">
              {reasons[3].tags?.map((tag) => (
                <span key={tag} className="text-xs font-bold text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-3 py-1.5">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Two small cards */}
          <div className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-teal-300 hover:shadow-sm transition-all duration-300 cursor-default">
            <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl mb-5">
              <i className="ri-user-heart-line text-lg text-teal-600" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">{reasons[4].title}</h3>
            <p className="text-slate-500 text-sm leading-relaxed">{reasons[4].description}</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-teal-300 hover:shadow-sm transition-all duration-300 cursor-default">
            <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl mb-5">
              <i className="ri-check-double-line text-lg text-teal-600" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2">{reasons[5].title}</h3>
            <p className="text-slate-500 text-sm leading-relaxed">{reasons[5].description}</p>
          </div>

          {/* Stat card */}
          <div className="bg-teal-600 rounded-2xl p-7 cursor-default flex flex-col justify-center items-center text-center">
            <p className="text-5xl font-extrabold text-white tracking-tight mb-2">15+</p>
            <p className="text-white font-bold text-sm mb-1">Years in Business</p>
            <p className="text-teal-200 text-xs">Serving DFW since 2009</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
